import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { Loader2, Send, Mail, User } from 'lucide-react';

interface NotificationPreviewModalProps {
  open: boolean;
  onClose: () => void;
  type: 'payment' | 'document' | 'departure';
  reminderId: string;
  onSend: () => void;
  sending: boolean;
}

export function NotificationPreviewModal({
  open,
  onClose,
  type,
  reminderId,
  onSend,
  sending,
}: NotificationPreviewModalProps) {
  const { t } = useTranslation('notifications');

  const { data, isLoading } = trpc.notifications.previewNotification.useQuery(
    { type, reminderId },
    { enabled: open }
  );

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[hsl(var(--gold))]" />
            {t('previewTitle')}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
          </div>
        ) : data ? (
          <div className="space-y-4">
            {/* Recipient Info */}
            <div className="flex items-center gap-3 p-4 rounded-lg bg-[hsl(var(--muted))]">
              <div className="rounded-full bg-[hsl(var(--background))] p-2">
                <User className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
              </div>
              <div>
                <p className="font-medium">{data.reminder.customerName}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {data.reminder.customerEmail}
                </p>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                {t('subject')}
              </label>
              <div className="mt-1 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                <p className="font-medium">{data.preview.subject}</p>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                {t('message')}
              </label>
              <div className="mt-1 p-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] max-h-[300px] overflow-y-auto">
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {data.preview.message}
                </pre>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
            Failed to load preview
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('close')}
          </Button>
          <Button onClick={onSend} disabled={sending || !data} className="gap-2">
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {t('sendNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
